import { Badge } from "../atoms/Badge/Badge.js";
import { Button } from "../atoms/Button/index.js";
import type { PlayerAIMessageReference } from "./playerai.types";

/**
 * Task 7 — a subtle one-line pointer to another message in the thread: the
 * message that started a Player AI response, or the Player AI response a user
 * message replies to.
 *
 * A softly tinted strip with no visible label or start-edge bar. One
 * truncated line; activating it takes the reader to the referenced message,
 * which the host loads first when it is outside the loaded history.
 */
export function MessageReferencePreview({
  reference,
  additionalCount = 0,
  onOpen,
}: {
  reference: PlayerAIMessageReference;
  /** How many more messages the same Run answered, shown as "+N". */
  additionalCount?: number;
  onOpen?: ((reference: PlayerAIMessageReference) => void) | undefined;
}) {
  const author = reference.authorName?.trim();
  const text = reference.text.replace(/\s+/g, " ").trim() || "Message";
  const more =
    additionalCount === 1
      ? " and 1 more message"
      : additionalCount > 1
        ? ` and ${additionalCount} more messages`
        : "";
  return (
    <Button
      variant="soft"
      tone="ai"
      size="xs"
      className="mb-2 w-full min-w-0 justify-start"
      disabled={onOpen === undefined}
      onClick={() => onOpen?.(reference)}
      aria-label={`Go to message: ${author ? `${author}: ` : ""}${text}${more}`}
      data-message-reference={reference.messageId}
    >
      <span className="min-w-0 flex-1 truncate text-start font-normal text-pb-text-muted">
        {text}
      </span>
      {additionalCount > 0 ? (
        <Badge tone="neutral" size="xs" shape="pill" className="shrink-0">
          +{additionalCount}
        </Badge>
      ) : null}
    </Button>
  );
}
